
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, User, Phone, Send, Building2 } from 'lucide-react';
import NavBar from '@/components/layout/NavBar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/components/i18n/language-provider';

const Contact = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Send actual email to sales@keystle.com
      const formToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formToSubmit.append(key, value);
      });
      
      // Add form destination and configuration
      formToSubmit.append('_to', 'sales@keystle.com');
      formToSubmit.append('_subject', `Contact Form Submission from ${formData.firstName} ${formData.lastName}`);
      formToSubmit.append('_captcha', 'true'); // Enable reCAPTCHA
      
      // Send to formsubmit.co service (which doesn't require API keys)
      const response = await fetch('https://formsubmit.co/ajax/sales@keystle.com', {
        method: 'POST',
        body: formToSubmit
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      await response.json();
      
      toast({
        title: "Form submitted successfully",
        description: "We'll get back to you soon.",
      });
      
      // Navigate to thank you page
      navigate('/contact/thank-you');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-grow container mx-auto px-4 pt-28 pb-16 md:pt-32"> {/* Increased top padding here */}
        <div className="max-w-3xl mx-auto bg-white dark:bg-background p-8 rounded-lg shadow-sm">
          <h1 className="text-4xl font-bold mb-6">Contact Us</h1>
          <p className="text-lg text-muted-foreground mb-10">
            Have questions about our products or services? Fill out the form below and our team will get back to you as soon as possible.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium mb-2">
                  First Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-muted-foreground">
                    <User size={18} />
                  </div>
                  <Input 
                    id="firstName"
                    name="firstName"
                    placeholder="Your first name"
                    className="pl-10"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium mb-2">
                  Last Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-muted-foreground">
                    <User size={18} />
                  </div>
                  <Input 
                    id="lastName"
                    name="lastName"
                    placeholder="Your last name"
                    className="pl-10"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label htmlFor="company" className="block text-sm font-medium mb-2">
                Company Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-muted-foreground">
                  <Building2 size={18} />
                </div>
                <Input 
                  id="company"
                  name="company"
                  placeholder="Your company name"
                  className="pl-10"
                  value={formData.company}
                  onChange={handleChange}
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                Business Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-muted-foreground">
                  <Mail size={18} />
                </div>
                <Input 
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your.email@company.com"
                  className="pl-10"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="phone" className="block text-sm font-medium mb-2">
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-muted-foreground">
                  <Phone size={18} />
                </div>
                <Input 
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="Your phone number"
                  className="pl-10"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-medium mb-2">
                How can we help you?
              </label>
              <Textarea 
                id="message"
                name="message"
                placeholder="Describe your needs..."
                rows={5}
                value={formData.message}
                onChange={handleChange}
                required
                className="resize-none"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></span>
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  Submit
                </span>
              )}
            </Button>
            
            <p className="text-xs text-muted-foreground text-center mt-2">
              This form is protected by reCAPTCHA to ensure you're not a bot. Your information is secure and will only be used to respond to your inquiry.
            </p>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
